import { Typography } from "@material-ui/core"
import { TagCategory } from "api"
import CloseIcon from "components/Icons/CloseIcon"
import { Ref, forwardRef } from "react"
import styled from "styled-components"
import colors from "theme/colors"
import { useCreateAiContext } from "./CreateAiContext"

const StyledTitleContainer = styled.div`
  ${(p) => p.theme.breakpoints.up("md")} {
    display: flex;
    align-items: center;
  }
`

const StyledTitle = styled(Typography).attrs({
  component: "h2",
  variant: "h2",
})`
  color: ${colors.black};
  white-space: nowrap;
  font-size: 26px;
  line-height: 0.6;
  font-family: Speedee, NarkisBlock, sans-serif;
  margin-bottom: 16px;
  text-align: right;
  position: relative;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-left: 16px;
    &:after {
      content: "";
      width: 1px;
      margin-right: 16px;
      height: 32px;
      position: absolute;
      background-color: ${colors.black};
      top: -10px;
    }
  }
`
const StyledExplanation = styled(Typography).attrs({
  variant: "body1",
})`
  font-size: 24px;
  font-weight: 100;
  color: #000;
  line-height: 0.92;
  text-align: right;
  margin-bottom: 16px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 18px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-right: 16px;
  }
`
const StyledPropertiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 40px;
  position: relative;
  ${(p) => p.theme.breakpoints.up("md")} {
    gap: 24px;
    margin-bottom: 60px;
  }
`
const StyledProperty = styled.button<{
  $isSelected: boolean
  $isNotSelected: boolean
}>`
  color: ${(p) => (p.$isSelected ? colors.darkGreen : colors.warmGray)};
  width: auto;
  height: 50px;

  padding: 0 ${(p) => (p.$isSelected ? `8px` : `16px`)};
  display: flex;
  font-weight: ${(p) => (p.$isSelected ? `700` : `400`)};
  align-items: center;
  opacity: ${(p) => (p.$isNotSelected ? `0.5` : `1`)};
  border: 1px solid ${(p) => (p.$isSelected ? "#FFBC0D" : "#E5E5E5")};
  border-radius: 8px;

  background: var(--White, #fff);
  ${(p) => p.theme.breakpoints.up("md")} {
    cursor: pointer;
  }
  font-family: "NarkisBlock-Thin";
  font-size: 24px;

  &:hover {
    border: 2px solid var(--Grey, #757575);
  }
`

const StyledError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  font-size: 18px;
  line-height: 1;
  position: absolute;
  bottom: -24px;
  margin: 0px;
`

const PropertiesAi = forwardRef(
  (
    {
      properties,
      checkValiation,
    }: {
      properties: TagCategory[]
      checkValiation: boolean
    },
    ref: Ref<HTMLButtonElement>
  ) => {
    const { propertiesData, setPropertiesData } = useCreateAiContext()
    return (
      <>
        <StyledTitleContainer>
          <StyledTitle>{properties[0]?.name}</StyledTitle>
          <StyledExplanation>
            יש לך אפשרות לבחור עד {properties[0]?.maxToSelect} מאפיינים{" "}
          </StyledExplanation>
        </StyledTitleContainer>
        <StyledPropertiesContainer>
          {properties[0]?.tags.map((property, i) => (
            <StyledProperty
              key={i}
              aria-label={
                propertiesData.length === 0 && checkValiation && i === 0
                  ? `   יש לבחור לפחות מאפיין אחד.${property.name} `
                  : !propertiesData?.includes(property.id)
                  ? property.name
                  : `לְהַסִיר תכונה ${property.name}`
              }
              ref={i === 0 ? ref : null} // Assign ref only to the first element
              $isSelected={propertiesData?.includes(property?.id)}
              $isNotSelected={
                propertiesData?.length === properties[0].maxToSelect &&
                !propertiesData?.includes(property.id)
              }
              onClick={() => {
                if (propertiesData?.includes(property.id)) {
                  setPropertiesData(
                    propertiesData?.filter((item) => item != property.id)
                  )
                  return
                }
                if (propertiesData?.length < properties[0]?.maxToSelect) {
                  setPropertiesData([...propertiesData, property.id])
                }
              }}
            >
              {propertiesData?.includes(property.id) && (
                <CloseIcon
                  onClick={() =>
                    setPropertiesData(
                      propertiesData?.filter((p) => p !== property.id)
                    )
                  }
                />
              )}
              {property.name}
            </StyledProperty>
          ))}
          {propertiesData.length === 0 && checkValiation && (
            <StyledError role="alert" aria-live="polite">
              יש לבחור לפחות מאפיין אחד
            </StyledError>
          )}
        </StyledPropertiesContainer>
      </>
    )
  }
)

PropertiesAi.displayName = "PropertiesAi"

export default PropertiesAi
